import { Directive, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

/**
 * Section headers viem appends below an error's one-line summary: call arguments, calldata,
 * RPC request details, docs link and library version. None of it is meant for the user.
 */
const VIEM_DETAIL_SECTION = /^\s*(Raw Call Arguments|Contract Call|Request Arguments|Estimate Gas Arguments|Request body|URL|Docs|Details|Version):/m;

/** Revert phrasings that carry the contract's own reason, from viem or straight from the node. */
const REVERT_WITH_REASON = /(?:reverted with the following reason:|execution reverted(?: with reason)?:)\s*(.+)$/i;

@Directive({
    selector: '[appErrorHandler]',
    standalone: false
})
@Injectable({ providedIn: 'root' })
export class ErrorHandlerDirective {

    constructor(
        private toastr: ToastrService,
    ) { }

    getErrorMessage(error: any, fallbackMessage?: string): string {
        const status = error?.data?.status || 500;

        // Extract shortMessage for easier access
        const isContractError = error?.name?.toLowerCase() === "contractfunctionexecutionerror";
        const apiMessage = error?.data?.message || error?.error?.message;
        const shortMessage = apiMessage || (isContractError ? error?.cause?.reason || error?.cause?.cause?.shortMessage : null);
        // shortMessage can be viem's full multi-line dump; it is only matched against, never shown.
        const displayMessage = apiMessage
            ? this.toConciseMessage(apiMessage)
            : isContractError ? this.toContractMessage(error) : null;

        // Plain `Error`s thrown by our own guards carry their text on `message` alone, which is
        // read nowhere above — they used to collapse into the generic message and lose the cause.
        // Restricted to `name === 'Error'` so viem/HTTP errors, whose `message` is a multi-line
        // dump, keep resolving through the existing branches untouched.
        const plainErrorMessage = error instanceof Error && error.name === 'Error' ? error.message : null;

        // Predefined error messages
        const userDeclinedMessage = 'User declined the transaction.';
        const genericErrorMessage = fallbackMessage || 'Something went wrong';
        const notFoundMessage = 'Requested route not found';
        const serviceUnavailableMessage = 'Service Unavailable';
        const insufficiantFund = "Insufficient funds"
        const insufficiant_gas ='gas: insufficient funds for gas * price + value'
        // Error Handling Switch
        switch (status) {
            case 4001:
                return userDeclinedMessage;
            case 404:
                return notFoundMessage;
            case 400:
            case 500:
                if (shortMessage?.includes('User rejected the request')) {
                    return userDeclinedMessage;
                } else if (
                    shortMessage?.includes("The total cost (gas * gas fee + value) of executing this transaction exceeds the balance of the account") ||
                    shortMessage?.includes(insufficiant_gas)
                ) {
                    return insufficiantFund;
                }
                return displayMessage || plainErrorMessage || genericErrorMessage;
            case 503:
                return serviceUnavailableMessage;
            default:
                return displayMessage || plainErrorMessage || genericErrorMessage;
        }
    }

    /**
     * Reduces a message to the summary viem puts before its first blank line, without the
     * call-argument, calldata and version sections, so it can be shown to a user.
     */
    private toConciseMessage(message: unknown): string | null {
        if (typeof message !== 'string') {
            return null;
        }

        let concise = message.split(/\n\s*\n/)[0];
        const detailStart = concise.search(VIEM_DETAIL_SECTION);
        if (detailStart >= 0) {
            concise = concise.slice(0, detailStart);
        }

        concise = concise.replace(/\s+/g, ' ').trim();
        return concise || null;
    }

    /**
     * Turns a failed contract call into "Execution reverted with reason: <reason>." when the
     * contract's reason can be identified, and into the concise summary otherwise.
     */
    private toContractMessage(error: any): string | null {
        const reverted = error?.cause;
        // viem decoded the revert data into the contract's own require message.
        const decodedReason = reverted?.data?.errorName === 'Error' ? reverted?.reason : null;
        const concise = this.toConciseMessage(reverted?.reason || reverted?.cause?.shortMessage);
        if (!concise) {
            return null;
        }

        const reason = (concise.match(REVERT_WITH_REASON)?.[1] ?? (decodedReason ? concise : null))
            ?.replace(/\.$/, '')
            .trim();
        return reason ? `Execution reverted with reason: ${reason}.` : concise;
    }

    /**
     * Handles error
     * @param {any} error
     */
    reportError(error: any, context?: string): void {
        if (context) {
            console.error(context, error);
            return;
        }

        console.error(error);
    }

    async handleError(error: any, fallbackMessage?: string, context?: string) {
        this.reportError(error, context);
        this.toastr.error(this.getErrorMessage(error, fallbackMessage));
    }


}
